import {
  ensureJourneyDb,
  getById,
  insertRow,
  rowsFromIndex,
  updateRow,
  writeAudit,
} from "../../db";
import { createAttachment, createMessage } from "../../db/schema";
import { assertThreadAccess, assertTransactionAccess, counterpartId, resolveActor } from "./access";
import { badRequest, conflict, notFound } from "./errors";
import { hydrateThread, hydrateTransaction, notifyUser } from "./helpers";
import { recordBetaFeedback } from "../betaFeedback";

/** POST /api/messages/send */
export function postMessagesSend(body = {}, session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const thread = getById("message_threads", body.thread_id);
  if (!thread) throw notFound("Thread not found");
  assertThreadAccess(actor, thread);
  if (thread.status === "archived") throw conflict("Thread is archived");

  const transaction = getById("transactions", thread.transaction_id);
  const senderId = body.sender_id || actor.userId;
  if (senderId !== actor.userId && !actor.isSupervisor) {
    throw badRequest("sender_id must match the signed-in user");
  }
  const receiverId = body.receiver_id || counterpartId(transaction, actor);
  const text = String(body.message_text || "").trim();
  if (!text && !(body.attachments || []).length) throw badRequest("message_text is required");

  const attachments = Array.isArray(body.attachments) ? body.attachments : [];
  const tag = body.tag === "betaFeedback" ? "betaFeedback" : null;
  const message = insertRow(
    "messages",
    createMessage({
      thread_id: thread.id,
      sender_id: senderId,
      receiver_id: receiverId,
      message_text: text,
      has_attachments: attachments.length > 0,
      tag,
    })
  );

  const savedAttachments = attachments.map((item) =>
    insertRow(
      "attachments",
      createAttachment({
        message_id: message.id,
        file_path: item.file_path || "",
        file_name: item.file_name || "attachment",
        mime_type: item.mime_type || "application/octet-stream",
      })
    )
  );

  writeAudit({
    user_id: actor.userId,
    action_type: "message.create",
    transaction_id: transaction.id,
    metadata: { thread_id: thread.id, message_id: message.id },
  });

  if (receiverId && receiverId !== senderId) {
    notifyUser({
      user_id: receiverId,
      type: "message",
      actor_id: actor.userId,
      transaction_id: transaction.id,
      payload: {
        thread_id: thread.id,
        message_id: message.id,
        transaction_id: transaction.id,
      },
    });
  }

  if (tag === "betaFeedback" && text) {
    try {
      recordBetaFeedback({
        session,
        message_text: text,
        message_id: message.id,
        thread_id: thread.id,
        mirrorToOffice: false,
      });
    } catch {
      /* table write is best-effort beside the tagged message */
    }
  }

  return {
    ...message,
    attachments: savedAttachments,
  };
}

/** POST /api/messages/thread/:thread_id/read */
export function postMessagesThreadRead(threadId, session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const thread = getById("message_threads", threadId);
  if (!thread) throw notFound("Thread not found");
  assertThreadAccess(actor, thread);
  rowsFromIndex("messages", "messagesByThreadId", threadId).forEach((message) => {
    if (message.receiver_id === actor.userId && !message.read_status) {
      updateRow("messages", message.id, { read_status: true });
    }
  });
  return hydrateThread(getById("message_threads", threadId));
}

/** GET /api/messages/thread/:thread_id */
export function getMessagesThread(threadId, session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const thread = getById("message_threads", threadId);
  if (!thread) throw notFound("Thread not found");
  assertThreadAccess(actor, thread);
  return hydrateThread(thread);
}

/** GET /api/messages/transaction/:transaction_id */
export function getMessagesTransaction(transactionId, session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const transaction = getById("transactions", transactionId);
  if (!transaction) throw notFound("Transaction not found");
  assertTransactionAccess(actor, transaction);
  return hydrateTransaction(transaction);
}
